import type {
  PlayerOrgRatingAllResponsePagedData,
  PlayerProfileResponse,
} from '@on3/api';
import { proxyApi } from '@on3/ui-lib/utils/api';
import type { GetServerSideProps } from 'next';
import { IndustryComparisonComponent } from 'src/components/IndustryComparison/IndustryComparisonComponent';
import { EmbedWrapper } from 'src/components/Layouts/EmbedWrapper';
import { PlayerHeader } from 'src/components/Player/PlayerHeader';

export interface IPlayerAllRankingPageProps {
  basicInfo?: PlayerProfileResponse;
  allRankingList?: PlayerOrgRatingAllResponsePagedData;
}

const IndustryComparison = ({
  basicInfo,
  allRankingList,
}: IPlayerAllRankingPageProps) => {
  const title = `${basicInfo?.name}'s Recruiting Industry Comparison`;
  const description = `${basicInfo?.name}'s rankings breakdown on the 4 major recruiting services: On3, 247sports, ESPN, and Rivals.`;

  return (
    <EmbedWrapper description={description} title={title}>
      <PlayerHeader basicInfo={basicInfo} />
      <IndustryComparisonComponent
        allRankingList={allRankingList}
        basicInfo={basicInfo}
      />
    </EmbedWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { id, sport } = ctx.query;
  const playerId = id?.toString().split('-').pop();
  let sportKey = 1;

  if (!playerId) {
    return {
      notFound: true,
    };
  }

  let data = {};

  try {
    const [player] = await Promise.allSettled([
      proxyApi
        .get(`/public/rdb/v1/player/${playerId}/profile`)
        .then((r) => r.data),
    ]);

    if (player.status !== 'fulfilled') {
      return {
        notFound: true,
      };
    }

    const fetchedPlayer = player.value;

    //Move to a utlilty function
    if (sport && !Array.isArray(sport)) {
      //Looks at url
      if (sport.toLowerCase() === 'basketball') {
        sportKey = 2;
      }
    } else if (player?.value?.defaultSport) {
      //Then looks at response defaultSport
      sportKey = player?.value?.defaultSport?.key || 1;
    } else if (player?.value?.sports && player?.value?.sports?.length > 0) {
      //Then looks at response sports
      sportKey = player.value?.sports[0].key || 1;
    } else {
      //Then defaults to football
      sportKey = 1;
    }

    const [allRankingList] = await Promise.allSettled([
      proxyApi
        .get(
          `/public/rdb/v2/player/${playerId}/all-rankings?sportKey=${sportKey}`,
        )
        .then((r) => r.data),
    ]);

    data = {
      basicInfo: fetchedPlayer,
      allRankingList:
        allRankingList.status === 'fulfilled' ? allRankingList.value : [],
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default IndustryComparison;
